import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApartmentOutlined, CloseOutlined, FileTextOutlined, FolderOutlined,
  LoadingOutlined, NodeIndexOutlined, TeamOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { WorkspaceProject } from './useOfficeEngine';
import {
  DevConvo, DevData, DevEmployee, DevEvent, DevFileNode, DevSummary,
  ConfirmHint, dvoTime, clip, roleColor,
} from './devteam-shared';
import { HoloHallDesign } from './devteam-designs-a';
import './DevOffice.css';

/* 呈现定案：只保留"全息展厅"。其余 9 种设计代码保留在
   devteam-designs-a/b.tsx（未引用即不打包），需要时再启用。 */

/* ============================================================
   技术研发公司 · 内部研发办公室（独立只读页）
   —— 完整镜像数字员工后台的研发实况（成员/任务/沟通/流程/文件），
   本页只用来"看"研发情况与进度，唯一例外是提醒用户有待确认事项
   （办理入口在 AI 指挥办公室的确认反馈卡，本页不可点开）。
   严禁跳转 /digital-office。后台接口零改动。
   ============================================================ */

/* ---------------- 只读数据总线（轮询后台） ---------------- */
function useDevOfficeData(project: WorkspaceProject): DevData {
  const [employees, setEmployees] = useState<DevEmployee[]>([]);
  const [summary, setSummary] = useState<DevSummary | null>(null);
  const [convos, setConvos] = useState<DevConvo[]>([]);
  const [events, setEvents] = useState<DevEvent[]>([]);
  const [files, setFiles] = useState<DevFileNode[]>([]);
  const [pending, setPending] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const tickRef = useRef(0);

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      const slow = tickRef.current % 5 === 0; // 文件/事件低频
      tickRef.current += 1;
      try {
        const [e, s, c] = await Promise.all([
          api.get('/digital-employees/employees', { params: { projectId: project.id } }),
          api.get(`/digital-employees/projects/${project.id}/status-summary`),
          api.get(`/digital-employees/projects/${project.id}/conversations`, { params: { limit: 40 } }),
        ]);
        if (!alive) return;
        setEmployees(Array.isArray(e.data) ? e.data : e.data?.items || []);
        setSummary(s.data || null);
        const list = Array.isArray(c.data) ? c.data : c.data?.items || [];
        setConvos(list);
        setPending(Number(s.data?.pendingConfirmations || 0));
        setLoaded(true);
      } catch { /* 下轮再试 */ }
      if (slow) {
        try {
          const [ev, f] = await Promise.all([
            api.get(`/digital-employees/projects/${project.id}/state-events`, { params: { limit: 30 } }),
            api.get(`/digital-employees/projects/${project.id}/files`),
          ]);
          if (!alive) return;
          setEvents(Array.isArray(ev.data) ? ev.data : ev.data?.items || []);
          setFiles(Array.isArray(f.data) ? f.data : f.data?.items || []);
        } catch { /* 下轮再试 */ }
      }
    };
    pull();
    const t = setInterval(pull, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [project.id]);

  const busyOf = useCallback((emp: DevEmployee) => {
    for (const m of summary?.members || []) {
      const hit = m.employeeId === emp.id || m.id === emp.id || (m.name && m.name === emp.name);
      if (!hit) continue;
      const text = m.displayTitle || m.current || m.displaySummary || '';
      if (text) return clip(text, 60);
    }
    return null;
  }, [summary]);

  return useMemo(() => ({
    project: { id: project.id, name: project.name },
    employees, summary, convos, events, files, pending, loaded, busyOf,
  }), [project.id, project.name, employees, summary, convos, events, files, pending, loaded, busyOf]);
}

/* ============================================================
   研发流程 · 轨道站点图（全新展示思路：竖向多轨道，
   每个角色一条轨道，节点按时间从上往下停靠成"站点"，
   跨轨道曲线=工作交接。与数字员工页的横向卡片时间线完全不同）
   ============================================================ */
interface FlowNode { id: string; kind: string; lane?: string; title: string; status?: string; time?: string; summary?: string; actor?: { name?: string; role?: string } | null; target?: { name?: string; role?: string } | null }
interface FlowEdge { id: string; from: string; to: string; label?: string }
interface FlowLane { id: string; label: string }
interface FlowData { anchorId?: string; lanes?: FlowLane[]; nodes: FlowNode[]; edges: FlowEdge[]; path?: { nodeIds?: string[]; edgeIds?: string[] } }

const KIND_META: Record<string, { label: string; color: string; icon: string }> = {
  conversation: { label: '沟通', color: '#22d3ee', icon: '💬' },
  employee: { label: '角色', color: '#60a5fa', icon: '👤' },
  task: { label: '任务', color: '#a78bfa', icon: '📌' },
  batch: { label: '派活批次', color: '#818cf8', icon: '🧭' },
  delegate_item: { label: '派活', color: '#fbbf24', icon: '🤝' },
  confirmation: { label: '确认', color: '#fb7185', icon: '❔' },
  session: { label: '干活', color: '#34d399', icon: '🔨' },
  state_event: { label: '状态', color: '#94a3b8', icon: '⚡' },
};
const kindMeta = (k?: string) => KIND_META[k || ''] || { label: k || '记录', color: '#94a3b8', icon: '•' };

const LANE_W = 236;
const ROW_H = 104;

export const FlowMetro: React.FC<{ projectId: string; convos: DevConvo[]; inline?: boolean }> = ({ projectId, inline }) => {
  const [flow, setFlow] = useState<FlowData | null>(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<FlowNode | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  /* 需求脉络（用户拍板）：后台流程图按"一次用户需求"开窗，只看最后一条会让人
     误以为全程只有两个角色参与。这里列出用户从第一次发起以来的每一次需求，
     默认最新一次，可切换逐一查看每次需求的完整干活流程。 */
  const [reqs, setReqs] = useState<DevConvo[] | null>(null);
  const [selReq, setSelReq] = useState<string>('');
  const [reqMenu, setReqMenu] = useState(false);

  useEffect(() => {
    api.get(`/digital-employees/projects/${projectId}/conversations`, { params: { limit: 200 } })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
        /* 用户发出的消息 = 一次需求（fromEmployee 为空即用户） */
        const users = list.filter((c: DevConvo) => !c.fromEmployee && c.id && !String(c.id).startsWith('opt-'));
        setReqs(users);
        if (users.length) setSelReq(users[users.length - 1].id);
      })
      .catch(() => setReqs([]));
  }, [projectId]);

  useEffect(() => {
    if (!selReq) return;
    setFlow(null);
    setSel(null);
    setErr('');
    api.get(`/digital-employees/conversations/${selReq}/flow`)
      .then((r) => { setFlow(r.data || null); })
      .catch((e) => setErr(e?.response?.data?.message || '流程数据加载失败'));
  }, [selReq]);

  /* 首次铺好后滚到最新环节（底部） */
  useEffect(() => {
    const el = boxRef.current;
    if (el && flow) el.scrollTop = el.scrollHeight;
  }, [flow]);

  const selReqIdx = reqs?.findIndex((r) => r.id === selReq) ?? -1;
  const selReqObj = selReqIdx >= 0 ? reqs![selReqIdx] : null;

  const layout = useMemo(() => {
    if (!flow?.nodes?.length) return null;
    /* 泳道维度定案（用户拍板）：横向只放"人物"——用户 / 项目经理 / 产品经理 /
       架构师 / 研发 / 测试 等所有参与角色。系统事件等无人物节点不进图，
       领导只关心团队干活的流程。纯前端按节点 actor 角色重映射，后台零改动。 */
    const ROLE_CN: Record<string, string> = {
      PM: '项目经理', PMgr: '产品经理', Architect: '架构师', Developer: '研发',
      Frontend: '前端', Backend: '后端', Tester: '测试', DevOps: '运维', Docs: '文档', UI: '设计',
    };
    const laneOfNode = (n: FlowNode): string | null => {
      if (n.lane === 'user') return '用户';
      const role = n.actor?.role || n.target?.role || '';
      if (role) return ROLE_CN[role] || role;
      return null; // 无人物 → 领导不关心，不进图
    };
    const sorted = [...flow.nodes]
      .sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')))
      .map((n) => ({ n, laneLabel: laneOfNode(n) }))
      .filter((x): x is { n: FlowNode; laneLabel: string } => !!x.laneLabel);
    const total = sorted.length;
    /* 真实项目节点可达数百个：只铺最近 60 个环节，保持可读 */
    const shown = sorted.slice(-60);
    const LANE_PREF = ['用户', '项目经理', '产品经理', '架构师', '研发', '前端', '后端', '测试', '运维', '文档', '设计'];
    const present = Array.from(new Set(shown.map((x) => x.laneLabel)));
    const laneLabels = [
      ...LANE_PREF.filter((l) => present.includes(l)),
      ...present.filter((l) => !LANE_PREF.includes(l)),
    ];
    const lanes: FlowLane[] = laneLabels.map((l) => ({ id: l, label: l }));
    const laneIdx = new Map(laneLabels.map((l, i) => [l, i]));
    const pos = new Map<string, { x: number; y: number }>();
    shown.forEach((x, row) => {
      const li = laneIdx.get(x.laneLabel) ?? 0;
      pos.set(x.n.id, { x: 96 + li * LANE_W, y: 84 + row * ROW_H });
    });
    return {
      lanes,
      nodes: shown.map((x) => x.n),
      pos,
      trimmed: total - shown.length,
      w: 96 + lanes.length * LANE_W + 60,
      h: 84 + shown.length * ROW_H + 60,
      onPath: new Set(flow.path?.nodeIds || []),
    };
  }, [flow]);

  return (
    <div className={`dvo-metro ${inline ? 'inline' : ''}`} onClick={() => { setSel(null); setReqMenu(false); }}>
      {/* 需求脉络选择：从第一次需求到最新，逐次查看 */}
      <div className="reqbar" onClick={(e) => e.stopPropagation()}>
        <button className="cur" onClick={() => setReqMenu((v) => !v)} disabled={!reqs?.length}>
          {reqs === null && <><LoadingOutlined /> 正在梳理需求脉络…</>}
          {reqs !== null && reqs.length === 0 && '该项目还没有用户需求记录'}
          {selReqObj && (
            <>
              <i className="no">第 {selReqIdx + 1} 次需求</i>
              <b>{clip(selReqObj.content, 34)}</b>
              <em>{dvoTime(selReqObj.createdAt)} ▾</em>
            </>
          )}
        </button>
        {reqMenu && reqs && reqs.length > 0 && (
          <div className="menu">
            {[...reqs].reverse().map((r, k) => (
              <button
                key={r.id}
                className={`ri ${r.id === selReq ? 'on' : ''}`}
                onClick={() => { setSelReq(r.id); setReqMenu(false); }}
              >
                <i>第 {reqs.length - k} 次</i>
                <b>{clip(r.content, 42)}</b>
                <em>{dvoTime(r.createdAt)}</em>
              </button>
            ))}
          </div>
        )}
      </div>
      {!flow && !err && reqs?.length !== 0 && <div className="dvo-empty pad"><LoadingOutlined /> 正在梳理研发流程…</div>}
      {err && <div className="dvo-empty pad">{err}</div>}
      {flow && !layout && <div className="dvo-empty pad">这条线索暂时没有流程记录</div>}
      {layout && (
        <div className="scrollbox" ref={boxRef}>
          {layout.trimmed > 0 && <div className="trimnote">已聚焦最近 60 个环节（更早的 {layout.trimmed} 个环节已折叠）</div>}
          <div className="board" style={{ width: layout.w, height: layout.h }}>
            {/* 轨道 */}
            {layout.lanes.map((l, i) => (
              <div key={l.id} className="rail" style={{ left: 96 + i * LANE_W }}>
                <b style={{ color: roleColor(l.label) }}>{l.label}</b>
                <i style={{ background: `linear-gradient(180deg, ${roleColor(l.label)}66, ${roleColor(l.label)}11)` }} />
              </div>
            ))}
            {/* 交接曲线 */}
            <svg className="wires" width={layout.w} height={layout.h}>
              {flow!.edges.map((e) => {
                const a = layout.pos.get(e.from);
                const b = layout.pos.get(e.to);
                if (!a || !b) return null;
                const mid = (a.y + b.y) / 2;
                return (
                  <path
                    key={e.id}
                    d={`M ${a.x} ${a.y + 17} C ${a.x} ${mid}, ${b.x} ${mid}, ${b.x} ${b.y - 17}`}
                    className={a.x === b.x ? 'same' : 'cross'}
                  />
                );
              })}
            </svg>
            {/* 站点 */}
            {layout.nodes.map((n) => {
              const p = layout.pos.get(n.id)!;
              const m = kindMeta(n.kind);
              const hot = layout.onPath.has(n.id) || n.status === 'anchor' || n.status === 'running';
              return (
                <div
                  key={n.id}
                  className={`station ${hot ? 'hot' : ''} ${n.status === 'failed' ? 'bad' : ''}`}
                  style={{ left: p.x, top: p.y, '--kc': m.color } as React.CSSProperties}
                  onClick={(ev) => { ev.stopPropagation(); setSel(n); }}
                >
                  <i className="dot">{m.icon}</i>
                  <div className="card">
                    <em>{m.label}{n.time ? ` · ${dvoTime(n.time)}` : ''}</em>
                    <b>{clip(n.title, 26)}</b>
                    {(n.actor?.name || n.target?.name) && (
                      <u>{n.actor?.name || ''}{n.target?.name ? ` → ${n.target.name}` : ''}</u>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* 站点详情（只读） */}
      {sel && (
        <div className="dvo-node-pop" onClick={(e) => e.stopPropagation()}>
          <div className="hd" style={{ '--kc': kindMeta(sel.kind).color } as React.CSSProperties}>
            <i>{kindMeta(sel.kind).icon}</i>
            <b>{sel.title}</b>
            <button onClick={() => setSel(null)}><CloseOutlined /></button>
          </div>
          <div className="meta">
            {sel.actor?.name && <span>发起：{sel.actor.name}（{sel.actor.role || '成员'}）</span>}
            {sel.target?.name && <span>对象：{sel.target.name}（{sel.target.role || '成员'}）</span>}
            {sel.time && <span>时间：{new Date(sel.time).toLocaleString('zh-CN')}</span>}
            {sel.status && <span>状态：{sel.status}</span>}
          </div>
          {sel.summary && <pre className="sum">{clip(sel.summary, 1200)}</pre>}
        </div>
      )}
    </div>
  );
};

/* ---------------- 产出文件（只读树） ---------------- */
const FileBranch: React.FC<{ node: DevFileNode; depth: number }> = ({ node, depth }) => {
  const [open, setOpen] = useState(depth < 1);
  const isDir = node.type === 'directory' || node.type === 'dir' || !!node.children?.length;
  return (
    <div className="fb" style={{ paddingLeft: depth ? 14 : 0 }}>
      <button className={`fr ${isDir ? 'dir' : ''}`} onClick={() => isDir && setOpen((v) => !v)}>
        {isDir ? <FolderOutlined className="ic d" /> : <FileTextOutlined className="ic" />}
        <span>{node.name}</span>
        {isDir && <em>{open ? '▾' : '▸'}</em>}
      </button>
      {isDir && open && (node.children || []).map((c, i) => (
        <FileBranch key={`${c.path || c.name}-${i}`} node={c} depth={depth + 1} />
      ))}
    </div>
  );
};

const FilesPanel: React.FC<{ D: DevData }> = ({ D }) => (
  <div className="dvo-files">
    {D.files.length === 0 && <div className="dvo-empty">项目工作区暂无产出文件</div>}
    {D.files.map((n, i) => <FileBranch key={`${n.path || n.name}-${i}`} node={n} depth={0} />)}
  </div>
);

/* ---------------- 团队动态（状态事件流） ---------------- */
const EventsPanel: React.FC<{ D: DevData }> = ({ D }) => (
  <div className="dvo-events">
    {D.events.length === 0 && <div className="dvo-empty">暂无团队动态</div>}
    {D.events.map((ev, i) => (
      <div key={ev.id || i} className={`row lv-${ev.level || 'info'}`}>
        <i className="tick" />
        <div className="bd">
          <div className="l1">
            <b style={{ color: roleColor(ev.employee?.role) }}>{ev.employee?.name || '系统'}</b>
            <u>{dvoTime(ev.createdAt)}</u>
          </div>
          <p>{clip(ev.message || ev.event, 110)}</p>
        </div>
      </div>
    ))}
  </div>
);

/* ============================================================
   外壳：顶栏 + 设计切换 + 通用抽屉（流程/文件/动态）+ 当前设计
   ============================================================ */
const DevTeamOffice: React.FC<{ project: WorkspaceProject; onClose: () => void }> = ({ project, onClose }) => {
  const D = useDevOfficeData(project);
  const [drawer, setDrawer] = useState<'flow' | 'files' | 'events' | null>(null);

  const running = (D.summary?.activeTasks || 0) + (D.summary?.runningDelegates || 0);

  return (
    <div className="dvo dvo-holo" onClick={(e) => e.stopPropagation()}>
      {/* 主舞台：全息展厅（定案单形态） */}
      <div className="dvo-stage">
        <HoloHallDesign D={D} />
      </div>

      {/* 顶栏（所有设计共用） */}
      <header className="dvo-top">
        <TeamOutlined className="ic" />
        <div className="tt">
          <b>技术研发公司 · 内部研发办公室</b>
          <span>项目「{D.project.name}」· 只读实况，交办请回指挥台</span>
        </div>
        <div className={`live ${running ? 'on' : ''}`}>
          {running ? <LoadingOutlined /> : <i className="dot" />}
          {D.summary?.displayText || (D.loaded ? '现场安静' : '正在进入办公室…')}
        </div>
        <div className="tools">
          <button className={drawer === 'flow' ? 'on' : ''} onClick={() => setDrawer((v) => (v === 'flow' ? null : 'flow'))}>
            <NodeIndexOutlined /> 研发流程
          </button>
          <button className={drawer === 'files' ? 'on' : ''} onClick={() => setDrawer((v) => (v === 'files' ? null : 'files'))}>
            <FolderOutlined /> 产出文件
          </button>
          <button className={drawer === 'events' ? 'on' : ''} onClick={() => setDrawer((v) => (v === 'events' ? null : 'events'))}>
            <ThunderboltOutlined /> 团队动态
          </button>
        </div>
        <button className="x" onClick={onClose}><CloseOutlined /> 返回研发进行时</button>
      </header>

      {/* 待确认提醒（只提醒，办理在指挥办公室） */}
      <ConfirmHint D={D} />

      {/* 通用抽屉：研发流程 / 产出文件 / 团队动态 */}
      {drawer && (
        <aside className="dvo-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="dh">
            <ApartmentOutlined />
            <b>{drawer === 'flow' ? '研发流程 · 轨道站点图' : drawer === 'files' ? '项目产出文件' : '团队动态'}</b>
            <span>{drawer === 'flow' ? '按角色分轨，自上而下即时间顺序' : drawer === 'files' ? '工作区实时文件（只读）' : '状态变化实录'}</span>
            <button className="x" onClick={() => setDrawer(null)}><CloseOutlined /></button>
          </div>
          <div className="db">
            {drawer === 'flow' && <FlowMetro projectId={D.project.id} convos={D.convos} />}
            {drawer === 'files' && <FilesPanel D={D} />}
            {drawer === 'events' && <EventsPanel D={D} />}
          </div>
        </aside>
      )}
    </div>
  );
};

export default DevTeamOffice;
